import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GetCart, RemoveCartItem, AddToCart, MoveItemToCartOrWishlist } from "../../Services/CartService";
import ProductImage from "../../assets/images/our-product.png";
import DeleteIcon from "../../assets/svg/delete.svg";
import EditIcon from "../../assets/svg/edit.svg";
import PlusIcon from "../../assets/svg/plus.svg";
import favoriteIcon from '../../assets/svg/favorite-red.svg';
import "../../assets/scss/component/_cart.scss";
import { getImageUrl, handleImageError } from "../../utils/imageUtils";

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cartSummary, setCartSummary] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCartItems();
  }, []);
  const handleRedirectEdit = (item) => {
    const queryParams = new URLSearchParams({
      type: "edit",
    }).toString();

    navigate(
      `/customize-wheat?${queryParams}`,
      { state: { cartItems: item } }
    )
  };

  const fetchCartItems = async () => {
    setLoading(true);
    setError(null);

    try {
      const userId = localStorage.getItem("userId");

      if (!userId) {
        setError("Please login to view cart");
        setLoading(false);
        return;
      }

      const response = await GetCart({
        shoppingCartTypeId: 2,
        userId: parseInt(userId),
      });

      if (response.status && response.data) {
        setCartItems(response.data);
        if (response.data.length > 0 && response.data[0].shoppingCartSummary) {
          setCartSummary(response.data[0].shoppingCartSummary);
        } else {
          setCartSummary(null);
        }
      } else {
        setError(response.message || "Failed to load cart items");
      }
    } catch (err) {
      console.error("Error fetching cart items:", err);
      setError("Failed to load cart items. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      const response = await RemoveCartItem(itemId);
      if (response.status) {
        fetchCartItems();
        window.dispatchEvent(new Event("cartUpdated"));
      } else {
        setError(response.message || "Failed to remove item");
      }
    } catch (error) {
      console.error("Error removing item:", error);
      setError("Failed to remove item. Please try again.");
    }
  };

  const handleAddItem = async (id) => {
    try {
      const item = cartItems.find((i) => i.id === id);
      const userId = localStorage.getItem("userId");

      const cartData = {
        productId: item.productId,
        shoppingCartTypeId: 2,
        quantity: 1,
        price: item.price,
        discountPrice: item.discountPrice,
        totalPrice: item.totalPrice,
        weight: item.weight,
        discountId: item.discountId || 0,
        attributesXml: item.attributeXml || "",
        userId: parseInt(userId),
      };

      const response = await AddToCart(cartData);

      if (response.status) {
        fetchCartItems();
        window.dispatchEvent(new Event("cartUpdated"));
      } else {
        setError(response.message || "Failed to add item");
      }
    } catch (error) {
      console.error("Error adding item:", error);
      setError("Failed to add item. Please try again.");
    }
  };

  const handleFavorite = async (itemId) => {
    try {
      const params = {
        cartItemId: parseInt(itemId),
        shoppingCartType: 1, // 1 = cart, 2 = wishlist (assuming)
      };

      const response = await MoveItemToCartOrWishlist(params);

      if (response?.status) {

        await fetchCartItems();

        window.dispatchEvent(new Event('cartUpdated'));
      }
    } catch (error) {
      console.error("Error moving item:", error);
    } finally {
    }
  };

  return (
    <div className="cart-container">
      <h3 className="cart-title">
        Cart ({cartSummary ? cartSummary.vegetableItems + cartSummary.wheatItems : 0})
      </h3>

      <div className="cart-main">
        {/* LEFT SIDE – Cart List */}
        <div className="cart-list">
          {loading ? (
            <p className="text-center mt-4">Loading...</p>
          ) : error ? (
            <p className="text-center text-danger mt-4">{error}</p>
          ) : cartItems.length === 0 ? (
            <div className="no-items text-center mt-5">
              <h5>No Item Found</h5>
            </div>
          ) : (
            cartItems.map((item) => (
              <div key={item.id} className="cart-item">
                {/* Product Info */}
                <div className="product-info">
                  <div className="product-image">
                    <img
                      src={getImageUrl(item.path, ProductImage)}
                      alt={item.productName}
                      onError={(e) => handleImageError(e, ProductImage)}
                      className="product-icon"
                    />
                  </div>

                  <div>
                    <h5 className="product-name">
                      {item.productName} {item.weight > 0 && `(${item.weight}KG)`}
                    </h5>

                    <div className="product-tags">
                      {item.attributes?.subProducts?.map((sp) => (
                        <span key={sp.id} className="tag">
                          {sp.name} {sp.weight}
                        </span>
                      ))}
                      {item.attributes?.attributes?.map((attr) => (
                        <span key={attr.id} className="tag">
                          {attr.value}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Quantity & Price & Delete */}
                <div className={'d-flex align-items-center gap-2 cart-total'}>
                  <p className={'fs_20 fw_700 text-black text-no-wrap'}>₹ {Number(item.totalPrice).toFixed(2)}</p>

                  <div className={'d-flex align-items-center gap-3'}>

                    <div
                      className={'action d-flex align-items-center justify-content-center'}
                      onClick={() => handleFavorite(item.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <img
                        src={favoriteIcon}
                        alt="favorite"
                        style={{ width: "16px", height: "16px" }}
                      />
                    </div>

                    {!item.isCustomProduct ? <div onClick={() => handleRedirectEdit(item)} style={{ cursor: 'pointer' }} className={'action d-flex align-items-center justify-content-center'}>
                      <img className={'img-fluid'} src={EditIcon} alt="edit icon" />
                    </div> : null}

                    <div
                      className={'action d-flex align-items-center justify-content-center'}
                      onClick={() => handleAddItem(item.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <img className={'img-fluid'} src={PlusIcon} alt="add icon" />
                    </div>

                    <div
                      className={'action d-flex align-items-center justify-content-center'}
                      onClick={() => handleRemoveItem(item.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <img className={'img-fluid'} src={DeleteIcon} alt="delete icon" style={{ width: "16px", height: "16px" }} />
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* RIGHT SIDE – Summary Box (always visible) */}
        <div className="summary-box">
          <h5 className="summary-title">SUMMARY</h5>

          <div className="summary-row">
            <span>Customize Wheat</span>
            <span>{cartSummary ? cartSummary.wheatItems : 0}</span>
          </div>

          <div className="summary-row">
            <span>Subtotal</span>
            <span>₹ {cartSummary ? Number(cartSummary.subTotal).toFixed(2) : 0}</span>
          </div>

          <div className="summary-row">
            <span>Discount</span>
            <span>₹ {cartSummary ? cartSummary.discount : 0}</span>
          </div>

          <div className="summary-row">
            <span>Taxes</span>
            <span>₹ 0</span>
          </div>

          <div className="summary-total">
            <span>Total:</span>
            <span>₹ {cartSummary ? Number(cartSummary.totalPrice).toFixed(2) : 0}</span>
          </div>

          <div className="summary-row mt-3">
            <p style={{ fontSize: "14px", fontWeight: "400", margin: 0 }}>
              Shipping, Tax and Shipping Protection not included
            </p>
          </div>

          {cartItems.length === 0 ? (
            <button className="payment-btn text-decoration-none text-white" disabled>
              Proceed To Payment
            </button>
          ) : (
            <button className="payment-btn" disabled={cartItems.length === 0}>
              <Link
                to="/order-payment"
                className="text-decoration-none text-white"
                state={{
                  itemIds: cartItems.map(item => item.id),
                  totalAmount: cartSummary ? cartSummary.totalPrice : 0,
                  couponCode: cartSummary ? cartSummary.couponCode : "",
                }}
              >
                Proceed To Payment
              </Link>
            </button>
          )}

          <p className="continue-shopping">
            <Link to="/shop" className="continue-shopping">
              Continue shopping
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Cart;